'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook, FaShoppingBag } from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';
import {
  beginReviewerOAuth,
  signInWithAppleWeb,
  type ReviewerAuthProvider,
} from '../utils/reviewerApi';
import { BRAND } from '../lib/brand';

// The app's own /terms and /privacy pages exist only on EscapeTo; on Camping
// Australia /terms redirected to the store homepage. There the store's
// Shopify policy pages are the real ones (both 200, checked 2026-10-18).
const LEGAL_LINKS =
  BRAND.key === 'escapeto'
    ? { terms: '/terms', privacy: '/privacy' }
    : { terms: '/policies/terms-of-service', privacy: '/policies/privacy-policy' };

// ── Props ─────────────────────────────────────────────────────

interface ReviewerLoginPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
  title?: string;
  subtitle?: string;
  description?: string;
  showSkip?: boolean;
  onSkip?: () => void;
}

// ── Component ─────────────────────────────────────────────────

const ReviewerLoginPrompt: React.FC<ReviewerLoginPromptProps> = ({
  isOpen,
  onClose,
  onLoginSuccess: _onLoginSuccess,
  title = 'Sign in to continue',
  subtitle,
  description,
  showSkip = false,
  onSkip,
}) => {
  const displaySubtitle = subtitle || description || 'We only use your name and email. No spam, ever.';
  const [visible, setVisible] = useState(false);
  const [loadingProvider, setLoadingProvider] =
    useState<ReviewerAuthProvider | null>(null);
  const [isAppleLoading, setIsAppleLoading] = useState(false);

  // Load Apple JS SDK — only once the prompt opens. The prompt is mounted on
  // every page (closed), so loading on mount fetched Apple's 42 KB SDK on every
  // page view for the few visitors who sign in (trace, 5 Oct 2026).
  useEffect(() => {
    if (!isOpen) return;
    if (document.getElementById('apple-auth-sdk')) return;
    const script = document.createElement('script');
    script.id = 'apple-auth-sdk';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.defer = true;
    document.head.appendChild(script);
  }, [isOpen]);

  // Animate in/out
  useEffect(() => {
    if (isOpen) {
      // Small delay so the CSS transition triggers
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  const handleOAuth = useCallback(
    async (provider: ReviewerAuthProvider) => {
      try {
        setLoadingProvider(provider);
        await beginReviewerOAuth(provider);
        // The browser will redirect; no need to close
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
        message.error(errorMessage);
        setLoadingProvider(null);
      }
    },
    [],
  );

  const handleAppleSignIn = async () => {
    setIsAppleLoading(true);
    try {
      if (!BRAND.appleWebClientId) {
        // Empty until the owner registers this brand's Apple Service ID
        // (see BrandConfig.appleWebClientId) — must not silently fall back
        // to another brand's client id, which used to mis-brand the sign-in
        // server-side (see user_scope.ESCAPETO_WEB_APPLE_AUD).
        throw new Error('Apple Sign In is not set up for this app yet.');
      }
      type AppleWindow = Window & { AppleID?: { auth: { init: (c: object) => void; signIn: () => Promise<{ authorization: { id_token: string }; user?: { name?: { firstName?: string; lastName?: string } } }> } } };
      // The SDK now starts loading when the prompt opens, so a fast tap can
      // land before it arrives: give it up to 5s rather than failing.
      for (let i = 0; i < 50 && !(window as AppleWindow).AppleID; i++) {
        await new Promise((r) => setTimeout(r, 100));
      }
      const AppleID = (window as AppleWindow).AppleID;
      if (!AppleID) {
        throw new Error('Apple Sign In is not available. Please try again.');
      }
      AppleID.auth.init({
        clientId: BRAND.appleWebClientId,
        scope: 'name email',
        redirectURI: typeof window !== 'undefined' ? window.location.origin : 'https://client.trip-planner.com.au',
        usePopup: true,
      });
      const response = await AppleID.auth.signIn();
      const idToken = response.authorization.id_token;
      const firstName = response.user?.name?.firstName ?? '';
      const lastName = response.user?.name?.lastName ?? '';
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      await signInWithAppleWeb(idToken, fullName || undefined);
      // Apple's popup signs in without leaving the page (Google redirects
      // back instead), so this is the one place the caller hears about it.
      _onLoginSuccess?.();
    } catch (err: unknown) {
      // User cancelled — Apple throws { error: 'popup_closed_by_user' }
      const appleErr = err as { error?: string };
      if (appleErr?.error !== 'popup_closed_by_user') {
        // Apple's own sheet often shows only a bare (!) when it fails, so
        // reassure the user their work is intact and offer the way forward.
        const detail =
          appleErr?.error || (err instanceof Error ? err.message : '');
        message.error(
          `Apple sign-in didn't complete${detail ? ` (${detail})` : ''}. Your details are safe — try another provider or continue as guest.`,
          6
        );
      }
      setIsAppleLoading(false);
    }
  };

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  // Escape closes the prompt (standard dialog behaviour).
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={handleBackdropClick}
    >
      {/* Backdrop — clicking it dismisses the prompt (the wrapper's own
          click handler never fires for these clicks because this div sits
          between it and the pointer). */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Card */}
      <div
        className={`relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-8 transform transition-all duration-300 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <IoCloseOutline size={24} />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
          <p className="text-sm text-gray-500">{displaySubtitle}</p>
        </div>

        {/* OAuth buttons */}
        <div className="flex flex-col gap-3">
          {/* Apple \u2014 rendered only once this brand has a configured Service
              ID (BRAND.appleWebClientId). Showing it with an empty id would
              always error on click; hiding it means the owner registering
              the Service ID is what turns it on, rather than a follow-up
              fix to un-hide it. */}
          {BRAND.appleWebClientId && (
            <button
              onClick={handleAppleSignIn}
              disabled={loadingProvider !== null || isAppleLoading}
              className="flex items-center justify-center w-full gap-3 px-4 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#000000' }}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="white" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              {isAppleLoading ? 'Signing in\u2026' : 'Continue with Apple'}
            </button>
          )}

          {/* Google */}
          <button
            onClick={() => handleOAuth('google')}
            disabled={loadingProvider !== null}
            className="flex items-center justify-center w-full gap-3 px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FcGoogle size={20} />
            {loadingProvider === 'google' ? 'Redirecting...' : 'Continue with Google'}
          </button>

          {/* Facebook */}
          <button
            onClick={() => handleOAuth('facebook')}
            disabled={loadingProvider !== null}
            className="flex items-center justify-center w-full gap-3 px-4 py-3 rounded-lg font-medium text-white hover:opacity-90 active:opacity-80 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#1877F2' }}
          >
            <FaFacebook size={20} />
            {loadingProvider === 'facebook'
              ? 'Redirecting...'
              : 'Continue with Facebook'}
          </button>

          {/* Shopify */}
          <button
            onClick={() => handleOAuth('shopify')}
            disabled={loadingProvider !== null}
            className="flex items-center justify-center w-full gap-3 px-4 py-3 rounded-lg font-medium text-white hover:opacity-90 active:opacity-80 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#96BF48' }}
          >
            <FaShoppingBag size={18} />
            {loadingProvider === 'shopify'
              ? 'Redirecting...'
              : 'Continue with Shopify'}
          </button>
        </div>

        {/* Privacy note */}
        <p className="text-xs text-gray-400 text-center mt-5 leading-relaxed">
          By signing in you agree to our{' '}
          <a href={LEGAL_LINKS.terms} className="underline hover:text-gray-600">
            Terms
          </a>{' '}
          and{' '}
          <a href={LEGAL_LINKS.privacy} className="underline hover:text-gray-600">
            Privacy Policy
          </a>
          .
        </p>

        {/* Skip / Cancel */}
        <div className="text-center mt-4 flex flex-col gap-2">
          {showSkip && onSkip && (
            <button
              onClick={onSkip}
              className="text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors"
            >
              Continue as guest
            </button>
          )}
          <button
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewerLoginPrompt;
